import { DocumentNode, ErrorPolicy, FetchPolicy } from "@apollo/client";
import { logger } from "../logger";
import { getApolloClient } from "./get-client";

// Retry configuration for network errors
const NETWORK_ERROR_RETRY_COUNT = 2;
const NETWORK_ERROR_RETRY_DELAY_MS = 1000; // 1 second between retries

// Helper to check if an error is a retryable network error
const isRetryableNetworkError = (error: unknown): boolean => {
	if (error && typeof error === "object" && "name" in error && error.name === "ApolloError") {
		const apolloError = error as {
			networkError?: {
				name?: string;
				message?: string;
			};
			message?: string;
		};

		// Retry on network errors (but not AbortError which is a timeout)
		const hasNetworkError = apolloError.networkError && apolloError.networkError.name !== "AbortError";

		const isFailedFetch =
			apolloError.message?.includes("Failed to fetch") ||
			apolloError.message?.includes("Network request failed") ||
			apolloError.message?.includes("Failed to connect");

		return !!(hasNetworkError || isFailedFetch);
	}
	return false;
};

// Helper to delay execution
const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// getClient uses defaultFetchPolicies for query/mutation
// you need to set fetchPolicy/errorPolicy if you want to override the default
export const getApolloData = async <T = unknown>({
	query,
	variables,
	fetchPolicy,
	errorPolicy,
	skip = false,
}: {
	query: DocumentNode;
	variables?: Record<string, unknown>;
	fetchPolicy?: FetchPolicy;
	errorPolicy?: ErrorPolicy;
	skip?: boolean | undefined;
}): Promise<{ data?: T; error?: unknown }> => {
	const client = getApolloClient();
	if (!client) return { error: "No Apollo Client" };

	let lastError: unknown;

	// Try with retries for network errors
	for (let attempt = 0; attempt <= NETWORK_ERROR_RETRY_COUNT; attempt++) {
		try {
			const { data, error } = await client.query({
				query,
				variables,
				...(fetchPolicy ? { fetchPolicy } : {}),
				...(errorPolicy ? { errorPolicy } : {}),
				...(skip ? { skip } : {}),
			});

			if (error) {
				// Enhanced error logging for debugging
				const errorContext: Record<string, unknown> = {
					errorMessage: error.message,
					variables: JSON.stringify(variables),
				};

				// Extract problematic IDs from GraphQL errors for SUP queries
				if (error.graphQLErrors) {
					error.graphQLErrors.forEach(gqlError => {
						if (gqlError.path && Array.isArray(gqlError.path)) {
							// Extract the index from paths like ["sellerUnitsAndProjects", "details", 3, "numOfUnits"]
							const detailIndex = gqlError.path[2];

							// Check if this is a SUP query with IDs or projectIds
							const params = variables?.params as Record<string, unknown> | undefined;
							const details = params?.details as Record<string, unknown> | undefined;
							const ids = details?.ids as string[] | undefined;
							const projectIds = details?.projectIds as string[] | undefined;

							if (typeof detailIndex === "number") {
								if (ids && ids[detailIndex]) {
									errorContext.problematicPropertyId = ids[detailIndex];
									errorContext.problematicIndex = detailIndex;
									errorContext.allPropertyIds = ids;
								} else if (projectIds && projectIds[detailIndex]) {
									errorContext.problematicProjectId = projectIds[detailIndex];
									errorContext.problematicIndex = detailIndex;
									errorContext.allProjectIds = projectIds;
								}
							}

							errorContext.errorPath = gqlError.path;
							errorContext.graphQLErrorMessage = gqlError.message;
						}
					});
				}

				logger.error("Failed to get data", errorContext);
			}

			return { data, error };
		} catch (e) {
			lastError = e;

			// Check if this is a retryable network error
			if (isRetryableNetworkError(e)) {
				// If we have retries left, wait and retry
				if (attempt < NETWORK_ERROR_RETRY_COUNT) {
					// eslint-disable-next-line no-console
					console.warn(
						`Network error on attempt ${attempt + 1}/${NETWORK_ERROR_RETRY_COUNT + 1}, retrying in ${NETWORK_ERROR_RETRY_DELAY_MS}ms...`,
					);
					await delay(NETWORK_ERROR_RETRY_DELAY_MS);
					continue;
				}

				// All retries exhausted - log the error now
				const apolloError = e as {
					networkError?: {
						name?: string;
						message?: string;
						statusCode?: number;
					};
					message?: string;
				};

				// eslint-disable-next-line no-console
				console.error("Apollo Network Error (after retries)", {
					errorMessage: apolloError.message,
					networkErrorName: apolloError.networkError?.name,
					networkErrorMessage: apolloError.networkError?.message,
					variables: variables,
					timestamp: new Date().toISOString(),
					retriesAttempted: NETWORK_ERROR_RETRY_COUNT,
					environment: {
						isClientSide: typeof window !== "undefined",
						userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "server",
						online: typeof navigator !== "undefined" ? navigator.onLine : "unknown",
					},
				});

				return { error: e };
			}

			// Check if this is an Apollo abort/timeout error - don't retry these
			if (e && typeof e === "object" && "name" in e && e.name === "ApolloError") {
				const apolloError = e as {
					networkError?: {
						name?: string;
					};
				};

				if (apolloError.networkError?.name === "AbortError") {
					// Timeout errors are not retried and not logged here (already logged in get-client.ts)
					return { error: e };
				}

				// Other Apollo errors (non-network): log and return
				const errorDetails: Record<string, unknown> = {
					errorType: e instanceof Error ? e.constructor.name : typeof e,
					errorMessage: e instanceof Error ? e.message : String(e),
					variables: variables,
					timestamp: new Date().toISOString(),
				};
				logger.error("Failed to get data (non-network)", errorDetails);
				return { error: e };
			}

			// For non-Apollo errors, log them (these are unexpected) and don't retry
			const errorDetails: Record<string, unknown> = {
				errorType: e instanceof Error ? e.constructor.name : typeof e,
				errorMessage: e instanceof Error ? e.message : String(e),
				variables: variables,
				timestamp: new Date().toISOString(),
			};

			// Add stack trace if available
			if (e instanceof Error && e.stack) {
				errorDetails.stack = e.stack;
			}

			logger.error("Failed to get data (unexpected)", errorDetails);
			return { error: e };
		}
	}

	// Should not reach here, but return last error just in case
	return { error: lastError };
};
